import { useState, useEffect, useRef, useCallback } from "react";
import PropTypes from "prop-types";
import useEmblaCarousel from "embla-carousel-react";
import Autoplay from "embla-carousel-autoplay";

const EXTRA_PADDING = 15;

function AutoScroll({ children, defaultPadding }) {
  const [isActive, setIsActive] = useState(false);
  const [slidePadding, setSlidePadding] = useState(defaultPadding);

  const containerRef = useRef(null);

  /**
   * Auto-scroll requires the last item to be completely hidden
   * => we add its width to the total available space
   * Extra padding ensures smooth looping on all screens (prevents autoplay from “snapping” at the end)
   */
  const calculatePadding = useCallback(() => {
    if (!containerRef.current) {
      return;
    }

    const slides = containerRef.current.children;
    const containerWidth = containerRef.current.offsetWidth;

    const slidesWidths = Array.from(slides).map((child) => child.children[0].offsetWidth);

    const slidesWidthsSum = slidesWidths.reduce((sum, width) => sum + width, 0);

    if (slidesWidthsSum > 0 && containerWidth > slidesWidthsSum) {
      const padding =
        Math.ceil(
          (containerWidth - slidesWidthsSum + slidesWidths[slidesWidths.length - 1]) /
            (slides.length - 1)
        ) + EXTRA_PADDING;

      setSlidePadding(padding);
    } else {
      setSlidePadding(defaultPadding);
    }
  }, [defaultPadding]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setIsActive(true);
    }, 2000);

    window.addEventListener("resize", calculatePadding);

    return () => {
      window.removeEventListener("resize", calculatePadding);
      clearTimeout(timeout);
    };
  }, []);

  const [viewportRef] = useEmblaCarousel(
    { loop: true, skipSnaps: true, duration: 10000, align: "start", active: isActive },
    [Autoplay({ delay: 0, stopOnInteraction: false })]
  );

  useEffect(() => {
    calculatePadding();
  }, [children]);

  return (
    <div className="w-full relative">
      <div className="overflow-hidden cursor-grab" ref={viewportRef}>
        <div className="flex items-center" ref={containerRef}>
          {children.props.children.map((child, idx) => (
            <div
              key={child.key || idx}
              className="flex-none w-[auto]"
              style={{
                paddingRight: `${slidePadding}px`,
              }}
            >
              {child}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

AutoScroll.propTypes = {
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]).isRequired,
  defaultPadding: PropTypes.number,
};

export default AutoScroll;
